import { logger } from '@/libs/logger';
import sleep from '@rhg/utils/sleep';
import { PropsWithChildren, createContext, useCallback, useState } from 'react';
import { LoadingDialog } from './loading-dialog';
import { LoadingDialogOptions } from './types';

export const DEFAULT_TIMEOUT = 3000;

export type { LoadingDialogOptions };

export interface LoadingDialogState {
    openLoadingDialog: (options?: LoadingDialogOptions) => void;
    closeLoadingDialog: () => void;
    isOpen: boolean;
    /** If true then we want to remove all content from the page while this dialog is open (See: components/page/index.tsx) */
    isInterstitial: boolean;
}

export const LoadingDialogContext = createContext<LoadingDialogState>({
    openLoadingDialog: () => null,
    closeLoadingDialog: () => null,
    isOpen: false,
    isInterstitial: false,
});

export const LoadingDialogProvider = ({ children }: PropsWithChildren) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isInterstitial, setIsInterstitial] = useState(false);
    const [options, setOptions] = useState<LoadingDialogOptions>();

    const onClose = useCallback((callback?: () => void) => {
        callback?.();
        setIsOpen(false);
        setIsInterstitial(false);
        setOptions(undefined);
    }, []);

    const closeLoadingDialog = useCallback(() => {
        onClose(options?.onClose);
    }, [onClose, options?.onClose]);

    const openLoadingDialog = useCallback(
        async (options?: LoadingDialogOptions) => {
            setOptions(options);
            if (options?.isInterstitial) {
                setIsInterstitial(true);
            }
            setIsOpen(true);
            if (!options?.isManuallyClosed) {
                const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
                await sleep(timeout);
                onClose(options?.onClose);
            }
        },
        [onClose]
    );

    // Memoized so the context value keeps a stable identity across renders.
    // Consumers depend on this in effect dependency arrays (e.g. reconsent-flow
    // container); an inline wrapper would change every render and loop forever.
    const handleOpenLoadingDialog = useCallback(
        (options?: LoadingDialogOptions) => {
            openLoadingDialog(options).catch((err) =>
                logger.error('Failed to open loading dialog', {}, err as Error)
            );
        },
        [openLoadingDialog]
    );

    return (
        <>
            <LoadingDialogContext.Provider
                value={{
                    openLoadingDialog: handleOpenLoadingDialog,
                    isOpen,
                    isInterstitial,
                    closeLoadingDialog,
                }}
            >
                {children}
            </LoadingDialogContext.Provider>
            <LoadingDialog
                isOpen={isOpen}
                options={{
                    ...options,
                    onClose: closeLoadingDialog,
                }}
            />
        </>
    );
};
